package com.ijoin.ui.screens

import com.ijoin.data.api.SettingsApi
import com.ijoin.data.model.Booking
import com.ijoin.data.model.CreateFreeTicketsRequest
import com.ijoin.data.model.Event
import com.ijoin.data.model.EventBookingResult
import com.ijoin.data.model.EventStatus
import com.ijoin.data.model.EventTicketBookingRequest
import com.ijoin.data.model.PlatformFee
import com.ijoin.data.model.TimeSlot
import com.ijoin.data.repository.BookingRepository
import com.ijoin.data.repository.EventRepository
import com.ijoin.data.repository.ServiceProviderRepository
import com.ijoin.data.repository.TicketRepository
import com.ijoin.data.repository.AuthRepository
import com.ijoin.data.model.isFreeEvent
import com.ijoin.i18n.Strings
import com.ijoin.i18n.globalStrings
import com.ijoin.ui.util.userFriendlyErrorMessage
import com.ijoin.util.datetime.combineDateAndTime
import com.ijoin.util.datetime.currentDateInTimeZone
import com.ijoin.util.datetime.currentDateTimeInTimeZone
import com.ijoin.util.datetime.parseTimeZoneOrDefault
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.daysUntil
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime

data class SlotBookingUiState(
    val isEnabled: Boolean = false,
    val calendarMonthStart: LocalDate? = null,
    val slotsByDate: Map<LocalDate, List<TimeSlot>> = emptyMap(),
    val isLoading: Boolean = false,
    val error: String? = null,
    val selectedDate: LocalDate? = null,
    val selectedSlotIds: Set<Long> = emptySet(),
    val isBooking: Boolean = false,
    val bookingError: String? = null,
    val bookingSuccess: Boolean = false,
    val lastBookingId: Long? = null,
    val issuedTicketId: String? = null,
    val slotCancellationError: String? = null,
    val dayCancellationError: String? = null,
    val isCancellingSlots: Boolean = false,
    val isCancellingDay: Boolean = false,
    val restoreSlotsError: String? = null,
    val restoreDayError: String? = null,
    val isRestoringSlots: Boolean = false,
    val isRestoringDay: Boolean = false,
)

enum class RecurringScheduleType { MONTHLY, WEEKLY }

data class RecurringBookingUiState(
    val isEnabled: Boolean = false,
    val scheduleType: RecurringScheduleType? = null,
    val calendarMonthStart: LocalDate? = null,
    val availableDaysOfWeek: Set<Int> = emptySet(),
    val selectedDate: LocalDate? = null,
    val isBooking: Boolean = false,
    val bookingError: String? = null,
    val bookingSuccess: Boolean = false,
    val lastOccurrenceDateTime: Instant? = null,
    val pendingPaymentBookingId: Long? = null,
    val capacityByDate: Map<LocalDate, Int> = emptyMap(),
    val isCapacityLoading: Boolean = false,
    val capacityError: String? = null,
    val issuedTicketId: String? = null,
    val blockedDates: Set<LocalDate> = emptySet(),
    val isCancellingDay: Boolean = false,
    val dayCancellationError: String? = null,
)

data class EventCardUiState(
    val event: Event? = null,
    val isFavorite: Boolean = false,
    val isLoading: Boolean = true,
    val error: String? = null,
    val platformFee: PlatformFee = PlatformFee.DEFAULT,
    val slotBooking: SlotBookingUiState = SlotBookingUiState(),
    val recurringBooking: RecurringBookingUiState = RecurringBookingUiState(),
    val oneTimeIsBooking: Boolean = false,
    val oneTimeBookingError: String? = null,
    val oneTimeBookingSuccess: Boolean = false,
    val oneTimePendingPaymentBookingId: Long? = null,
    val isCancellingEvent: Boolean = false,
    val cancelEventError: String? = null,
    val isRestoringEvent: Boolean = false,
    val restoreEventError: String? = null,
    val oneTimeRemainingTickets: Int? = null,
    val isOneTimeCapacityLoading: Boolean = false,
    val oneTimeCapacityError: String? = null,
    val isDeletingEvent: Boolean = false,
    val deleteEventError: String? = null,
    val eventDeleted: Boolean = false,
)

class EventCardViewModel(
    private val eventId: String,
) {
    private val eventRepository = EventRepository()
    private val bookingRepository = BookingRepository()
    private val serviceProviderRepository = ServiceProviderRepository()
    private val ticketRepository = TicketRepository()
    private val authRepository = AuthRepository()
    private val viewModelScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

    private val _uiState = MutableStateFlow(EventCardUiState())
    val uiState: StateFlow<EventCardUiState> = _uiState.asStateFlow()
    private val strings: Strings
        get() = globalStrings()

    init {
        loadEvent()
    }

    private fun loadEvent() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }

            val eventResult = eventRepository.getEventById(eventId)
            val favoriteResult = if (authRepository.isAuthenticated()) {
                eventRepository.isFavorite(eventId)
            } else {
                Result.success(false)
            }
            // Load platform fee (cached)
            val platformFee = SettingsApi.getPlatformFeeCached()

            if (eventResult.isSuccess && favoriteResult.isSuccess) {
                val event = eventResult.getOrNull()
                val effectivePlatformFee = event?.organizerPlatformFeePercent?.let { percent ->
                    platformFee.copy(feePercent = percent)
                } ?: platformFee
                val isFavorite = favoriteResult.getOrNull() ?: false
                val isSlotBooking = event?.scheduleType == "SLOT_BOOKING"
                val isEventCancelled = event?.status == EventStatus.CANCELLED || event?.status == EventStatus.DELETED
                val recurringType = when (event?.scheduleType) {
                    "MONTHLY" -> RecurringScheduleType.MONTHLY
                    "WEEKLY" -> RecurringScheduleType.WEEKLY
                    else -> null
                }
                val recurringDays = event?.selectedDaysOfWeek?.map { normalizeDayOfWeek(it) }?.toSet().orEmpty()
                val slotBookingEnabled = isSlotBooking && !isEventCancelled
                val recurringEnabled = recurringType != null && recurringDays.isNotEmpty() && !isEventCancelled

                _uiState.update {
                    it.copy(
                        event = event,
                        isFavorite = isFavorite,
                        isLoading = false,
                        platformFee = effectivePlatformFee,
                        isCancellingEvent = false,
                        cancelEventError = null,
                        isRestoringEvent = false,
                        restoreEventError = null,
                        slotBooking = if (slotBookingEnabled) {
                            val today = currentDateInEventZone(event)
                            val monthStart = firstDayOfMonth(today)
                            it.slotBooking.copy(
                                isEnabled = true,
                                calendarMonthStart = monthStart,
                                selectedDate = today,
                                slotsByDate = emptyMap(),
                                error = null,
                                selectedSlotIds = emptySet(),
                                bookingError = null,
                                bookingSuccess = false,
                                slotCancellationError = null,
                                dayCancellationError = null,
                                isCancellingSlots = false,
                                isCancellingDay = false,
                                restoreSlotsError = null,
                                restoreDayError = null,
                                isRestoringSlots = false,
                                isRestoringDay = false,
                                issuedTicketId = null,
                            )
                        } else {
                            SlotBookingUiState()
                        },
                        recurringBooking = if (recurringEnabled) {
                            val today = currentDateInEventZone(event)
                            var monthStart = firstDayOfMonth(today)
                            val occurrenceTime = when (recurringType) {
                                RecurringScheduleType.MONTHLY -> event?.monthlyTime
                                RecurringScheduleType.WEEKLY -> event?.weeklyTime
                                null -> null
                            } ?: event?.eventTime
                            val dateValidator = createOccurrenceValidator(event, occurrenceTime)
                            var initialSelection = findFirstAvailableDate(monthStart, recurringDays, today, dateValidator)
                            if (initialSelection == null) {
                                val nextMonthStart = monthStart.plus(DatePeriod(months = 1))
                                val nextSelection = findFirstAvailableDate(
                                    nextMonthStart,
                                    recurringDays,
                                    nextMonthStart,
                                    dateValidator,
                                )
                                if (nextSelection != null) {
                                    monthStart = nextMonthStart
                                    initialSelection = nextSelection
                                }
                            }

                            RecurringBookingUiState(
                                isEnabled = true,
                                scheduleType = recurringType,
                                calendarMonthStart = monthStart,
                                availableDaysOfWeek = recurringDays,
                                selectedDate = initialSelection,
                                isBooking = false,
                                bookingError = null,
                                bookingSuccess = false,
                            )
                        } else {
                            RecurringBookingUiState()
                        },
                    )
                }

                val slotMonthStart = _uiState.value.slotBooking.calendarMonthStart
                if (isSlotBooking && slotMonthStart != null) {
                    loadSlotsForMonth(slotMonthStart)
                }
                val loadedEvent = _uiState.value.event
                if (loadedEvent != null) {
                    loadOneTimeCapacity(loadedEvent)
                    val recurringState = _uiState.value.recurringBooking
                    val recurringMonth = recurringState.calendarMonthStart
                    if (recurringState.isEnabled && recurringMonth != null) {
                        loadRecurringCapacities(loadedEvent, recurringMonth, recurringState)
                    }
                }
            } else {
                val error = eventResult.exceptionOrNull()?.message
                    ?: favoriteResult.exceptionOrNull()?.message
                    ?: strings.unknownErrorMessage
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        error = error,
                    )
                }
            }
        }
    }

    private fun loadOneTimeCapacity(event: Event) {
        val occurrence = event.eventDate ?: return
        if (event.maxCapacity == null || event.maxCapacity <= 0) {
            return
        }
        viewModelScope.launch {
            _uiState.update {
                it.copy(
                    isOneTimeCapacityLoading = true,
                    oneTimeCapacityError = null,
                )
            }
            eventRepository.getOccurrenceCapacity(event.id, occurrence)
                .onSuccess { capacity ->
                    val fallback = event.availableTickets ?: event.maxCapacity
                    val remaining = capacity.remainingTickets ?: fallback
                    _uiState.update { state ->
                        state.copy(
                            isOneTimeCapacityLoading = false,
                            oneTimeCapacityError = null,
                            oneTimeRemainingTickets = remaining,
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update { state ->
                        state.copy(
                            isOneTimeCapacityLoading = false,
                            oneTimeCapacityError = error.message,
                        )
                    }
                }
        }
    }

    private fun loadRecurringCapacities(
        event: Event,
        monthStart: LocalDate,
        stateSnapshot: RecurringBookingUiState
    ) {
        if (!stateSnapshot.isEnabled) return
        val availableDays = stateSnapshot.availableDaysOfWeek
        if (availableDays.isEmpty()) {
            _uiState.update { state ->
                state.copy(
                    recurringBooking = state.recurringBooking.copy(
                        capacityByDate = emptyMap(),
                        capacityError = null,
                        isCapacityLoading = false,
                        blockedDates = emptySet(),
                        dayCancellationError = null,
                        isCancellingDay = false,
                    ),
                )
            }
            return
        }
        val occurrenceTime = when (stateSnapshot.scheduleType) {
            RecurringScheduleType.MONTHLY -> event.monthlyTime
            RecurringScheduleType.WEEKLY -> event.weeklyTime
            null -> null
        } ?: event.eventTime
        val today = currentDateInEventZone(event)
        val dateValidator = createOccurrenceValidator(event, occurrenceTime)
        val occurrences = buildOccurrencesForMonth(monthStart, availableDays, occurrenceTime, event)
        if (occurrences.isEmpty()) {
            _uiState.update { state ->
                state.copy(
                    recurringBooking = state.recurringBooking.copy(
                        capacityByDate = emptyMap(),
                        capacityError = null,
                        isCapacityLoading = false,
                        blockedDates = emptySet(),
                        dayCancellationError = null,
                        isCancellingDay = false,
                    ),
                )
            }
            return
        }
        viewModelScope.launch {
            _uiState.update { state ->
                state.copy(
                    recurringBooking = state.recurringBooking.copy(
                        isCapacityLoading = true,
                        capacityError = null,
                    ),
                )
            }
            val capacityZone = parseTimeZoneOrDefault(event.timeZone)
            eventRepository.getOccurrencesCapacity(event.id, occurrences)
                .onSuccess { capacities ->
                    val mapped = capacities.mapNotNull { dto ->
                        val date = runCatching { dto.occurrenceDateTime.toLocalDateTime(capacityZone).date }.getOrNull()
                        val remaining = dto.remainingTickets
                            ?: dto.maxCapacity
                            ?: event.maxCapacity
                        if (date != null && remaining != null) {
                            date to remaining
                        } else {
                            null
                        }
                    }.toMap()
                    val manualBlocked = stateSnapshot.blockedDates.filter {
                        it.year == monthStart.year && it.month == monthStart.month
                    }.associateWith { 0 }
                    val merged = manualBlocked.toMutableMap()
                    mapped.forEach { (date, remaining) ->
                        merged[date] = remaining
                    }
                    val blockedDates = merged.filterValues { it <= 0 }.keys
                    _uiState.update { state ->
                        val available = state.recurringBooking.availableDaysOfWeek
                        val preservedSelection = state.recurringBooking.selectedDate?.takeIf { selected ->
                            selected.year == monthStart.year &&
                                selected.month == monthStart.month &&
                                !selected.isBefore(today) &&
                                available.contains(selected.dayOfWeek.isoDayNumber - 1) &&
                                !blockedDates.contains(selected) &&
                                dateValidator(selected)
                        }
                        val sanitizedSelection = preservedSelection
                            ?: findFirstAvailableDate(
                                monthStart,
                                available,
                                maxOfDate(monthStart, today),
                                { candidate -> !blockedDates.contains(candidate) && dateValidator(candidate) },
                            )
                        state.copy(
                            recurringBooking = state.recurringBooking.copy(
                                isCapacityLoading = false,
                                capacityError = null,
                                capacityByDate = merged,
                                blockedDates = blockedDates,
                                selectedDate = sanitizedSelection,
                            ),
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update { state ->
                        state.copy(
                            recurringBooking = state.recurringBooking.copy(
                                isCapacityLoading = false,
                                capacityError = error.message,
                            ),
                        )
                    }
                }
        }
    }

    fun bookOneTime(quantity: Int) {
        val event = _uiState.value.event ?: return
        val occurrence = event.eventDate
        if (occurrence == null) {
            _uiState.update {
                it.copy(
                    oneTimeIsBooking = false,
                    oneTimeBookingError = strings.missingEventDateError,
                    oneTimeBookingSuccess = false,
                    oneTimePendingPaymentBookingId = null,
                )
            }
            return
        }
        _uiState.update {
            it.copy(
                oneTimeIsBooking = true,
                oneTimeBookingError = null,
                oneTimeBookingSuccess = false,
                oneTimePendingPaymentBookingId = null,
            )
        }
        val requestedQuantity = quantity.coerceAtLeast(1)
        val available = _uiState.value.oneTimeRemainingTickets?.takeIf { it > 0 }
        val resolvedQuantity = available?.let { max -> requestedQuantity.coerceAtMost(max) } ?: requestedQuantity

        viewModelScope.launch {
            val request = EventTicketBookingRequest(
                eventId = eventId,
                quantity = resolvedQuantity,
                priceTierName = event.priceTiers?.firstOrNull()?.name,
                holderName = null,
                holderEmail = null,
                occurrenceDateTime = occurrence,
            )
            val result = bookingRepository.bookEventTickets(request)
            result
                .onSuccess { bookingResult ->
                    _uiState.update {
                        it.copy(
                            oneTimeIsBooking = false,
                            oneTimeBookingError = null,
                            oneTimeBookingSuccess = bookingResult is EventBookingResult.Reservation,
                            oneTimePendingPaymentBookingId = (bookingResult as? EventBookingResult.Reservation)?.booking?.id,
                        )
                    }
                    _uiState.value.event?.let { currentEvent ->
                        loadOneTimeCapacity(currentEvent)
                    }
                }
                .onFailure { e ->
                    _uiState.update {
                        it.copy(
                            oneTimeIsBooking = false,
                            oneTimeBookingError = userFriendlyErrorMessage(
                                e,
                                strings.bookingRequestFailed,
                            ),
                            oneTimeBookingSuccess = false,
                            oneTimePendingPaymentBookingId = null,
                        )
                    }
                }
        }
    }

    fun onOneTimeBookingHandled() {
        _uiState.update {
            it.copy(
                oneTimeBookingSuccess = false,
                oneTimePendingPaymentBookingId = null,
            )
        }
    }

    private fun normalizeDayOfWeek(day: Int): Int {
        val normalized = day % 7
        return if (normalized < 0) normalized + 7 else normalized
    }

    private fun firstDayOfMonth(date: LocalDate): LocalDate {
        return LocalDate(year = date.year, monthNumber = date.monthNumber, dayOfMonth = 1)
    }

    private fun findFirstAvailableDate(
        monthStart: LocalDate,
        availableDays: Set<Int>,
        minDate: LocalDate? = null,
        isDateAllowed: (LocalDate) -> Boolean = { true },
    ): LocalDate? {
        if (availableDays.isEmpty()) return null

        val daysInMonth = monthStart.daysUntil(monthStart.plus(DatePeriod(months = 1)))
        val startIndex = when {
            minDate == null -> 0
            minDate.year != monthStart.year || minDate.month != monthStart.month -> 0
            else -> minDate.dayOfMonth - 1
        }

        for (offset in startIndex until daysInMonth) {
            val date = monthStart.plus(DatePeriod(days = offset))
            if (availableDays.contains(date.dayOfWeek.isoDayNumber - 1) &&
                (minDate == null || !date.isBefore(minDate)) &&
                isDateAllowed(date)) {
                return date
            }
        }

        for (offset in 0 until startIndex) {
            val date = monthStart.plus(DatePeriod(days = offset))
            if (availableDays.contains(date.dayOfWeek.isoDayNumber - 1) &&
                (minDate == null || !date.isBefore(minDate)) &&
                isDateAllowed(date)) {
                return date
            }
        }

        return null
    }

    private fun LocalDate.isBefore(other: LocalDate): Boolean = this < other

    private fun maxOfDate(first: LocalDate, second: LocalDate): LocalDate = if (first >= second) first else second

    private fun eventTimeZone(event: Event?): TimeZone = parseTimeZoneOrDefault(event?.timeZone)

    private fun currentDateInEventZone(event: Event?): LocalDate =
        currentDateInTimeZone(event?.timeZone)

    private fun currentDateTimeInEventZone(event: Event?): LocalDateTime =
        currentDateTimeInTimeZone(event?.timeZone)

    private fun isOccurrenceInPast(
        date: LocalDate,
        event: Event?,
        occurrenceTime: String?,
        nowDateTime: LocalDateTime = currentDateTimeInEventZone(event),
    ): Boolean {
        val time = occurrenceTime?.takeIf { it.isNotBlank() } ?: return false
        val occurrenceDateTime = combineDateAndTime(date, time)
        return occurrenceDateTime < nowDateTime
    }

    private fun createOccurrenceValidator(
        event: Event?,
        occurrenceTime: String?,
        referenceDateTime: LocalDateTime = currentDateTimeInEventZone(event),
    ): (LocalDate) -> Boolean = { date ->
        !isOccurrenceInPast(date, event, occurrenceTime, referenceDateTime)
    }

    private fun buildOccurrencesForMonth(
        monthStart: LocalDate,
        availableDays: Set<Int>,
        occurrenceTime: String?,
        event: Event,
    ): List<Instant> {
        if (availableDays.isEmpty()) return emptyList()
        val nowDateTime = currentDateTimeInEventZone(event)
        val today = nowDateTime.date
        val daysInMonth = monthStart.daysUntil(monthStart.plus(DatePeriod(months = 1)))
        val eventTimeZone = parseTimeZoneOrDefault(event.timeZone)
        val results = mutableListOf<Instant>()
        repeat(daysInMonth) { offset ->
            val date = monthStart.plus(DatePeriod(days = offset))
            val isPast = date.isBefore(today) || isOccurrenceInPast(date, event, occurrenceTime, nowDateTime)
            if (!isPast && availableDays.contains(date.dayOfWeek.isoDayNumber - 1)) {
                results.add(combineDateAndTime(date, occurrenceTime).toInstant(eventTimeZone))
            }
        }
        return results
    }

    private fun loadSlotsForMonth(monthStart: LocalDate) {
        viewModelScope.launch {
            _uiState.update { state ->
                if (!state.slotBooking.isEnabled) {
                    state
                } else {
                    state.copy(
                        slotBooking = state.slotBooking.copy(
                            isLoading = true,
                            error = null,
                            calendarMonthStart = monthStart,
                            bookingError = null,
                            bookingSuccess = false,
                            selectedSlotIds = emptySet(),
                            slotCancellationError = null,
                            dayCancellationError = null,
                            isCancellingSlots = false,
                            isCancellingDay = false,
                            restoreSlotsError = null,
                            restoreDayError = null,
                            isRestoringSlots = false,
                            isRestoringDay = false,
                        ),
                    )
                }
            }

            val result = bookingRepository.getAvailableSlots(
                eventId = eventId,
                monthStart = monthStart,
                eventTimeZoneId = _uiState.value.event?.timeZone
            )

            result
                .onSuccess { slotsByDate ->
                    val currentState = _uiState.value.slotBooking
                    val currentSelected = currentState.selectedDate
                    val availableDates = slotsByDate.keys.sorted()
                    val newSelected = when {
                        currentSelected != null && availableDates.contains(currentSelected) -> currentSelected
                        availableDates.isNotEmpty() -> availableDates.first()
                        else -> null
                    }

                    _uiState.update { state ->
                        state.copy(
                            slotBooking = state.slotBooking.copy(
                                isLoading = false,
                                error = null,
                                slotsByDate = slotsByDate,
                                selectedDate = newSelected,
                                selectedSlotIds = emptySet(),
                                slotCancellationError = null,
                                dayCancellationError = null,
                                isCancellingSlots = false,
                                isCancellingDay = false,
                                restoreSlotsError = null,
                                restoreDayError = null,
                                isRestoringSlots = false,
                                isRestoringDay = false,
                            ),
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update { state ->
                        state.copy(
                            slotBooking = state.slotBooking.copy(
                                isLoading = false,
                                error = error.message ?: strings.slotLoadFailed,
                            ),
                        )
                    }
                }
        }
    }

    fun loadPreviousMonth() {
        val monthStart = _uiState.value.slotBooking.calendarMonthStart ?: return
        val previousMonth = monthStart.minus(DatePeriod(months = 1))
        val currentMonthStart = firstDayOfMonth(currentDateInEventZone(_uiState.value.event))
        if (previousMonth < currentMonthStart) {
            return
        }
        loadSlotsForMonth(previousMonth)
    }

    fun loadNextMonth() {
        val monthStart = _uiState.value.slotBooking.calendarMonthStart ?: return
        loadSlotsForMonth(monthStart.plus(DatePeriod(months = 1)))
    }

    fun loadRecurringPreviousMonth() {
        val monthStart = _uiState.value.recurringBooking.calendarMonthStart ?: return
        val previousMonth = monthStart.minus(DatePeriod(months = 1))
        val currentMonthStart = firstDayOfMonth(currentDateInEventZone(_uiState.value.event))
        if (previousMonth < currentMonthStart) {
            return
        }
        updateRecurringMonth(previousMonth)
    }

    fun loadRecurringNextMonth() {
        val monthStart = _uiState.value.recurringBooking.calendarMonthStart ?: return
        updateRecurringMonth(monthStart.plus(DatePeriod(months = 1)))
    }

    private fun updateRecurringMonth(monthStart: LocalDate) {
        var updatedState: RecurringBookingUiState? = null
        _uiState.update { state ->
            if (!state.recurringBooking.isEnabled) {
                state
            } else {
                val availableDays = state.recurringBooking.availableDaysOfWeek
                val event = state.event
                val scheduleType = state.recurringBooking.scheduleType
                val occurrenceTime = when (scheduleType) {
                    RecurringScheduleType.MONTHLY -> event?.monthlyTime
                    RecurringScheduleType.WEEKLY -> event?.weeklyTime
                    null -> null
                } ?: event?.eventTime
                val today = currentDateInEventZone(event)
                val nowDateTime = currentDateTimeInEventZone(event)
                val dateValidator = createOccurrenceValidator(event, occurrenceTime, nowDateTime)
                val blockedDates = state.recurringBooking.blockedDates.filter {
                    it.year == monthStart.year && it.month == monthStart.month
                }.toSet()
                val preservedSelection = state.recurringBooking.selectedDate?.takeIf { selected ->
                    selected.year == monthStart.year &&
                        selected.month == monthStart.month &&
                        !selected.isBefore(today) &&
                        availableDays.contains(selected.dayOfWeek.isoDayNumber - 1) &&
                        !blockedDates.contains(selected) &&
                        dateValidator(selected)
                }
                val dateAllowed: (LocalDate) -> Boolean = { candidate ->
                    !blockedDates.contains(candidate) && dateValidator(candidate)
                }
                val newSelected = preservedSelection
                    ?: findFirstAvailableDate(
                        monthStart,
                        availableDays,
                        maxOfDate(monthStart, today),
                        dateAllowed,
                    )
                val nextState = state.recurringBooking.copy(
                    calendarMonthStart = monthStart,
                    selectedDate = newSelected,
                    bookingError = null,
                    bookingSuccess = false,
                    capacityByDate = emptyMap(),
                    capacityError = null,
                    blockedDates = blockedDates,
                )
                updatedState = nextState
                state.copy(recurringBooking = nextState)
            }
        }
        val event = _uiState.value.event
        val snapshot = updatedState
        if (event != null && snapshot != null) {
            loadRecurringCapacities(event, monthStart, snapshot)
        }
    }

    fun selectDate(date: LocalDate) {
        _uiState.update { state ->
            if (!state.slotBooking.isEnabled) {
                state
            } else {
                val today = currentDateInEventZone(state.event)
                if (date.isBefore(today)) {
                    state
                } else {
                    state.copy(
                        slotBooking = state.slotBooking.copy(
                            selectedDate = date,
                            selectedSlotIds = emptySet(),
                            bookingError = null,
                            bookingSuccess = false,
                            slotCancellationError = null,
                            dayCancellationError = null,
                            restoreSlotsError = null,
                            restoreDayError = null,
                        ),
                    )
                }
            }
        }
    }

    fun selectRecurringDate(date: LocalDate) {
        _uiState.update { state ->
            if (!state.recurringBooking.isEnabled) {
                state
            } else {
                val availableDays = state.recurringBooking.availableDaysOfWeek
                val event = state.event
                val scheduleType = state.recurringBooking.scheduleType
                val occurrenceTime = when (scheduleType) {
                    RecurringScheduleType.MONTHLY -> event?.monthlyTime
                    RecurringScheduleType.WEEKLY -> event?.weeklyTime
                    null -> null
                } ?: event?.eventTime
                val today = currentDateInEventZone(event)
                val nowDateTime = currentDateTimeInEventZone(event)
                val isPast = date.isBefore(today) || isOccurrenceInPast(date, event, occurrenceTime, nowDateTime)
                if (isPast) {
                    state.copy(
                        recurringBooking = state.recurringBooking.copy(
                            bookingError = strings.selectedDateInPastError,
                            bookingSuccess = false,
                        ),
                    )
                } else if (!availableDays.contains(date.dayOfWeek.isoDayNumber - 1)) {
                    state.copy(
                        recurringBooking = state.recurringBooking.copy(
                            bookingError = strings.selectedDateUnavailableError,
                            bookingSuccess = false,
                        ),
                    )
                } else {
                    state.copy(
                        recurringBooking = state.recurringBooking.copy(
                            selectedDate = date,
                            bookingError = null,
                            bookingSuccess = false,
                        ),
                    )
                }
            }
        }
    }

    fun toggleSlot(slotId: Long) {
        _uiState.update { state ->
            if (!state.slotBooking.isEnabled) {
                state
            } else {
                val updatedSelection = state.slotBooking.selectedSlotIds.toMutableSet().also { selection ->
                    if (!selection.add(slotId)) {
                        selection.remove(slotId)
                    }
                }
                state.copy(
                    slotBooking = state.slotBooking.copy(
                        selectedSlotIds = updatedSelection,
                        bookingError = null,
                        bookingSuccess = false,
                        slotCancellationError = null,
                        restoreSlotsError = null,
                    ),
                )
            }
        }
    }

    fun bookRecurringOccurrence(quantity: Int) {
        val event = _uiState.value.event ?: return
        val recurringState = _uiState.value.recurringBooking
        if (!recurringState.isEnabled) return

        val selectedDate = recurringState.selectedDate
        if (selectedDate == null) {
            _uiState.update { state ->
                state.copy(
                    recurringBooking = state.recurringBooking.copy(
                        bookingError = strings.missingDate,
                        bookingSuccess = false,
                    ),
                )
            }
            return
        }

        if (!recurringState.availableDaysOfWeek.contains(selectedDate.dayOfWeek.isoDayNumber - 1)) {
            _uiState.update { state ->
                state.copy(
                        recurringBooking = state.recurringBooking.copy(
                            bookingError = strings.selectedDateUnavailableError,
                        bookingSuccess = false,
                    ),
                )
            }
            return
        }

        val requestedQuantity = quantity.coerceAtLeast(1)
        val resolvedQuantity = if (recurringState.scheduleType == RecurringScheduleType.WEEKLY) {
            val capacityForDay = recurringState.selectedDate?.let { date ->
                recurringState.capacityByDate[date]
            }
            if (capacityForDay != null && capacityForDay > 0) {
                requestedQuantity.coerceAtMost(capacityForDay)
            } else {
                requestedQuantity
            }
        } else {
            1
        }

        viewModelScope.launch {
            _uiState.update { state ->
                state.copy(
                    recurringBooking = state.recurringBooking.copy(
                        isBooking = true,
                        bookingError = null,
                        bookingSuccess = false,
                        pendingPaymentBookingId = null,
                        issuedTicketId = null,
                    ),
                )
            }

            val occurrenceTime = when (recurringState.scheduleType) {
                RecurringScheduleType.MONTHLY -> event.monthlyTime
                RecurringScheduleType.WEEKLY -> event.weeklyTime
                null -> null
            } ?: event.eventTime

            val occurrenceDateTime = combineDateAndTime(
                selectedDate,
                occurrenceTime,
            )

            val nowDateTime = currentDateTimeInEventZone(event)

            if (occurrenceDateTime < nowDateTime) {
                _uiState.update { state ->
                    state.copy(
                        recurringBooking = state.recurringBooking.copy(
                            isBooking = false,
                            bookingError = strings.selectedDateInPastError,
                            bookingSuccess = false,
                        ),
                    )
                }
                return@launch
            }

            val eventTimeZone = parseTimeZoneOrDefault(event.timeZone)
            val occurrenceInstant = occurrenceDateTime.toInstant(eventTimeZone)

            val request = EventTicketBookingRequest(
                eventId = eventId,
                quantity = resolvedQuantity,
                priceTierName = event.priceTiers?.firstOrNull()?.name,
                holderName = null,
                holderEmail = null,
                occurrenceDateTime = occurrenceInstant,
            )

            val result = bookingRepository.bookEventTickets(request)

            result
                .onSuccess { bookingResult ->
                    _uiState.update { state ->
                        val issuedTicketId = if (bookingResult is EventBookingResult.TicketsIssued) {
                            bookingResult.tickets.firstOrNull()?.id
                        } else null
                        val bookingId = when (bookingResult) {
                            is EventBookingResult.Reservation -> bookingResult.booking.id
                            is EventBookingResult.TicketsIssued ->
                                bookingResult.tickets.firstOrNull()?.bookingId?.toLongOrNull()
                        }
                        state.copy(
                            recurringBooking = state.recurringBooking.copy(
                                isBooking = false,
                                bookingError = null,
                                bookingSuccess = bookingResult is EventBookingResult.Reservation,
                                lastOccurrenceDateTime = occurrenceInstant,
                                pendingPaymentBookingId = bookingId,
                                issuedTicketId = issuedTicketId,
                            ),
                        )
                    }
                    val currentEvent = _uiState.value.event
                    val snapshotState = _uiState.value.recurringBooking
                    val month = snapshotState.calendarMonthStart
                    if (currentEvent != null && month != null) {
                        loadRecurringCapacities(currentEvent, month, snapshotState)
                    }
                }
                .onFailure { error ->
                    _uiState.update { state ->
                        state.copy(
                            recurringBooking = state.recurringBooking.copy(
                                isBooking = false,
                                bookingError = userFriendlyErrorMessage(
                                    error,
                                    strings.bookingRequestFailed,
                                ),
                                bookingSuccess = false,
                                pendingPaymentBookingId = null,
                                issuedTicketId = null,
                            ),
                        )
                    }
                }
        }
    }

    fun onRecurringBookingHandled() {
        _uiState.update { state ->
            state.copy(
                recurringBooking = state.recurringBooking.copy(
                    bookingSuccess = false,
                    pendingPaymentBookingId = null,
                    issuedTicketId = null,
                ),
            )
        }
    }

    fun onRecurringTicketHandled() {
        _uiState.update { state ->
            state.copy(
                recurringBooking = state.recurringBooking.copy(
                    issuedTicketId = null,
                ),
            )
        }
    }

    fun bookSelectedSlots() {
        val slotState = _uiState.value.slotBooking
        if (!slotState.isEnabled) return
        val currentEvent = _uiState.value.event

        if (slotState.selectedSlotIds.isEmpty()) {
            _uiState.update { state ->
                state.copy(
                        slotBooking = state.slotBooking.copy(
                            bookingError = strings.slotSelectionRequired,
                    ),
                )
            }
            return
        }

        val selectedSlotIds = slotState.selectedSlotIds.sorted()
        val selectedSlotCount = selectedSlotIds.size
        val priceTierName = currentEvent?.priceTiers?.firstOrNull()?.name
        val isFreeSlotEvent = currentEvent?.isFreeEvent() == true

        viewModelScope.launch {
            _uiState.update { state ->
                state.copy(
                    slotBooking = state.slotBooking.copy(
                        isBooking = true,
                        bookingError = null,
                        bookingSuccess = false,
                        issuedTicketId = null,
                        lastBookingId = null,
                    ),
                )
            }

            val result = bookingRepository.createBooking(
                eventId = eventId,
                slotIds = selectedSlotIds,
            )

            result
                .onSuccess { booking ->
                    if (isFreeSlotEvent) {
                        issueFreeTicketsForSlots(
                            booking = booking,
                            quantity = selectedSlotCount,
                            priceTierName = priceTierName,
                        )
                    } else {
                        _uiState.update { state ->
                            state.copy(
                                slotBooking = state.slotBooking.copy(
                                    isBooking = false,
                                    bookingError = null,
                                    bookingSuccess = true,
                                    selectedSlotIds = emptySet(),
                                    lastBookingId = booking.id,
                                    issuedTicketId = null,
                                ),
                            )
                        }
                    }
                }
                .onFailure { error ->
                    _uiState.update { state ->
                        state.copy(
                            slotBooking = state.slotBooking.copy(
                                isBooking = false,
                                bookingError = userFriendlyErrorMessage(
                                    error,
                                    strings.slotBookingFailed,
                                ),
                                issuedTicketId = null,
                            ),
                        )
                    }
                }
        }
    }

    private suspend fun issueFreeTicketsForSlots(
        booking: Booking,
        quantity: Int,
        priceTierName: String?
    ) {
        val userId = authRepository.getUserId()
        if (userId == null) {
            _uiState.update { state ->
                state.copy(
                        slotBooking = state.slotBooking.copy(
                            isBooking = false,
                            bookingError = strings.userContextMissingError,
                        bookingSuccess = false,
                        issuedTicketId = null,
                    ),
                )
            }
            return
        }

        val request = CreateFreeTicketsRequest(
            bookingId = booking.id,
            eventId = booking.eventId ?: eventId,
            userId = userId,
            quantity = if (quantity > 0) quantity else 1,
            priceTierName = priceTierName,
            occurrenceDateTime = booking.occurrenceDateTime,
        )

        ticketRepository.issueFreeTickets(request)
            .onSuccess { tickets ->
                val issuedTicketId = tickets.firstOrNull()?.id
                _uiState.update { state ->
                    state.copy(
                        slotBooking = state.slotBooking.copy(
                            isBooking = false,
                            bookingError = null,
                            bookingSuccess = true,
                            selectedSlotIds = emptySet(),
                            lastBookingId = booking.id,
                            issuedTicketId = issuedTicketId,
                        ),
                    )
                }
            }
            .onFailure { error ->
                _uiState.update { state ->
                    state.copy(
                        slotBooking = state.slotBooking.copy(
                            isBooking = false,
                            bookingError = userFriendlyErrorMessage(
                                error,
                                strings.ticketIssuanceFailed,
                            ),
                            bookingSuccess = false,
                            issuedTicketId = null,
                        ),
                    )
                }
            }
    }

    fun cancelSelectedSlots(reason: String) {
        val slotState = _uiState.value.slotBooking
        if (!slotState.isEnabled) return
        if (slotState.selectedSlotIds.isEmpty()) {
            _uiState.update { state ->
                state.copy(
                        slotBooking = state.slotBooking.copy(
                            slotCancellationError = strings.cancelSlotsSelectionRequired,
                    ),
                )
            }
            return
        }

        viewModelScope.launch {
            _uiState.update { state ->
                state.copy(
                    slotBooking = state.slotBooking.copy(
                        isCancellingSlots = true,
                        slotCancellationError = null,
                        restoreSlotsError = null,
                    ),
                )
            }

            val result = serviceProviderRepository.cancelSlots(
                eventId = eventId,
                slotIds = slotState.selectedSlotIds.toList(),
                reason = reason,
            )

            result
                .onSuccess {
                    _uiState.update { state ->
                        state.copy(
                            slotBooking = state.slotBooking.copy(
                                isCancellingSlots = false,
                                slotCancellationError = null,
                                selectedSlotIds = emptySet(),
                                restoreSlotsError = null,
                            ),
                        )
                    }
                    _uiState.value.slotBooking.calendarMonthStart?.let { loadSlotsForMonth(it) }
                }
                .onFailure { error ->
                    _uiState.update { state ->
                        state.copy(
                            slotBooking = state.slotBooking.copy(
                                isCancellingSlots = false,
                                slotCancellationError = error.message ?: strings.cancelSlotsFailed,
                            ),
                        )
                    }
                }
        }
    }

    fun cancelSlotDay(reason: String) {
        val slotState = _uiState.value.slotBooking
        if (!slotState.isEnabled) return
        val date = slotState.selectedDate
        if (date == null) {
            _uiState.update { state ->
                state.copy(
                        slotBooking = state.slotBooking.copy(
                            dayCancellationError = strings.cancelDaySelectionRequired,
                    ),
                )
            }
            return
        }

        viewModelScope.launch {
            _uiState.update { state ->
                state.copy(
                    slotBooking = state.slotBooking.copy(
                        isCancellingDay = true,
                        dayCancellationError = null,
                        restoreDayError = null,
                    ),
                )
            }

            val result = serviceProviderRepository.cancelSlotDay(
                eventId = eventId,
                date = date,
                reason = reason,
            )

            result
                .onSuccess {
                    _uiState.update { state ->
                        state.copy(
                            slotBooking = state.slotBooking.copy(
                                isCancellingDay = false,
                                dayCancellationError = null,
                                selectedSlotIds = emptySet(),
                                restoreDayError = null,
                            ),
                        )
                    }
                    _uiState.value.slotBooking.calendarMonthStart?.let { loadSlotsForMonth(it) }
                }
                .onFailure { error ->
                    _uiState.update { state ->
                        state.copy(
                            slotBooking = state.slotBooking.copy(
                                isCancellingDay = false,
                                dayCancellationError = error.message ?: strings.cancelDayFailed,
                            ),
                        )
                    }
                }
        }
    }

    fun cancelRecurringDay(reason: String) {
        val recurringState = _uiState.value.recurringBooking
        if (!recurringState.isEnabled) return
        val date = recurringState.selectedDate
        if (date == null) {
            _uiState.update { state ->
                state.copy(
                    recurringBooking = state.recurringBooking.copy(
                        dayCancellationError = strings.cancelDaySelectionRequired,
                    ),
                )
            }
            return
        }
        viewModelScope.launch {
            _uiState.update { state ->
                state.copy(
                    recurringBooking = state.recurringBooking.copy(
                        isCancellingDay = true,
                        dayCancellationError = null,
                    ),
                )
            }
            val result = serviceProviderRepository.cancelSlotDay(
                eventId = eventId,
                date = date,
                reason = reason,
            )
            result
                .onSuccess {
                    _uiState.update { state ->
                        val updatedBlocked = state.recurringBooking.blockedDates + date
                        val updatedCapacities = state.recurringBooking.capacityByDate.toMutableMap().apply {
                            put(date, 0)
                        }.toMap()
                        state.copy(
                            recurringBooking = state.recurringBooking.copy(
                                isCancellingDay = false,
                                dayCancellationError = null,
                                blockedDates = updatedBlocked,
                                capacityByDate = updatedCapacities,
                            ),
                        )
                    }
                    _uiState.value.event?.let { event ->
                        val monthStart = _uiState.value.recurringBooking.calendarMonthStart
                            ?: firstDayOfMonth(date)
                        loadRecurringCapacities(event, monthStart, _uiState.value.recurringBooking)
                    }
                }
                .onFailure { error ->
                    _uiState.update { state ->
                        state.copy(
                            recurringBooking = state.recurringBooking.copy(
                                isCancellingDay = false,
                                dayCancellationError = error.message ?: strings.cancelDayFailed,
                            ),
                        )
                    }
                }
        }
    }

    fun restoreSelectedSlots() {
        val slotState = _uiState.value.slotBooking
        if (!slotState.isEnabled) return
        if (slotState.selectedSlotIds.isEmpty()) {
            _uiState.update { state ->
                state.copy(
                        slotBooking = state.slotBooking.copy(
                            restoreSlotsError = strings.restoreSlotsSelectionRequired,
                    ),
                )
            }
            return
        }

        viewModelScope.launch {
            _uiState.update { state ->
                state.copy(
                    slotBooking = state.slotBooking.copy(
                        isRestoringSlots = true,
                        restoreSlotsError = null,
                    ),
                )
            }

            serviceProviderRepository.restoreSlots(eventId, slotState.selectedSlotIds.toList())
                .onSuccess {
                    _uiState.update { state ->
                        state.copy(
                            slotBooking = state.slotBooking.copy(
                                isRestoringSlots = false,
                                restoreSlotsError = null,
                                selectedSlotIds = emptySet(),
                            ),
                        )
                    }
                    _uiState.value.slotBooking.calendarMonthStart?.let { loadSlotsForMonth(it) }
                }
                .onFailure { error ->
                    _uiState.update { state ->
                        state.copy(
                            slotBooking = state.slotBooking.copy(
                                isRestoringSlots = false,
                                restoreSlotsError = error.message ?: strings.restoreSlotsFailed,
                            ),
                        )
                    }
                }
        }
    }

    fun restoreSlotDay() {
        val slotState = _uiState.value.slotBooking
        val date = slotState.selectedDate
        if (!slotState.isEnabled || date == null) {
            _uiState.update { state ->
                state.copy(
                        slotBooking = state.slotBooking.copy(
                            restoreDayError = strings.restoreDaySelectionRequired,
                    ),
                )
            }
            return
        }

        viewModelScope.launch {
            _uiState.update { state ->
                state.copy(
                    slotBooking = state.slotBooking.copy(
                        isRestoringDay = true,
                        restoreDayError = null,
                    ),
                )
            }

            serviceProviderRepository.restoreSlotDay(eventId, date)
                .onSuccess {
                    _uiState.update { state ->
                        state.copy(
                            slotBooking = state.slotBooking.copy(
                                isRestoringDay = false,
                                restoreDayError = null,
                                selectedSlotIds = emptySet(),
                            ),
                        )
                    }
                    _uiState.value.slotBooking.calendarMonthStart?.let { loadSlotsForMonth(it) }
                }
                .onFailure { error ->
                    _uiState.update { state ->
                        state.copy(
                            slotBooking = state.slotBooking.copy(
                                isRestoringDay = false,
                                restoreDayError = error.message ?: strings.restoreDayFailed,
                            ),
                        )
                    }
                }
        }
    }

    fun onBookingHandled() {
        _uiState.update { state ->
            state.copy(
                slotBooking = state.slotBooking.copy(
                    bookingSuccess = false,
                    lastBookingId = null,
                    issuedTicketId = null,
                ),
            )
        }
    }

    fun toggleFavorite() {
        viewModelScope.launch {
            val currentFavoriteState = _uiState.value.isFavorite
            val result = eventRepository.toggleFavorite(eventId, currentFavoriteState)

            result
                .onSuccess {
                    _uiState.update { state ->
                        state.copy(
                            isFavorite = !currentFavoriteState,
                            error = null,
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update { state ->
                        state.copy(error = error.message)
                    }
                }
        }
    }

    fun cancelEvent(reason: String) {
        viewModelScope.launch {
            _uiState.update { state ->
                state.copy(
                    isCancellingEvent = true,
                    cancelEventError = null,
                )
            }

            val result = eventRepository.cancelEvent(eventId, reason)
            result
                .onSuccess {
                    _uiState.update { state ->
                        state.copy(
                            isCancellingEvent = false,
                            cancelEventError = null,
                        )
                    }
                    loadEvent()
                }
                .onFailure { error ->
                    _uiState.update { state ->
                        state.copy(
                            isCancellingEvent = false,
                            cancelEventError = error.message ?: strings.cancelEventFailed,
                        )
                    }
                }
        }
    }

    fun restoreEvent() {
        viewModelScope.launch {
            _uiState.update { state ->
                state.copy(
                    isRestoringEvent = true,
                    restoreEventError = null,
                )
            }

            eventRepository.restoreEvent(eventId)
                .onSuccess {
                    _uiState.update { state ->
                        state.copy(
                            isRestoringEvent = false,
                            restoreEventError = null,
                        )
                    }
                    loadEvent()
                }
                .onFailure { error ->
                    _uiState.update { state ->
                        state.copy(
                            isRestoringEvent = false,
                            restoreEventError = error.message ?: strings.restoreEventFailed,
                        )
                    }
                }
        }
    }

    fun deleteEvent() {
        viewModelScope.launch {
            _uiState.update { state ->
                state.copy(
                    isDeletingEvent = true,
                    deleteEventError = null,
                )
            }

            eventRepository.deleteEvent(eventId)
                .onSuccess {
                    _uiState.update { state ->
                        state.copy(
                            isDeletingEvent = false,
                            deleteEventError = null,
                            eventDeleted = true,
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update { state ->
                        state.copy(
                            isDeletingEvent = false,
                            deleteEventError = error.message ?: strings.deleteEventFailed,
                        )
                    }
                }
        }
    }

    fun clearDeleteError() {
        _uiState.update { it.copy(deleteEventError = null) }
    }

    fun retry() {
        loadEvent()
    }
}
